import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListSkeleton } from "@/components/mobile/MobileSkeleton";
import { TextField } from "@/components/poshan/FormBits";
import ItemwiseConsumption from "@/components/poshan/ItemwiseConsumption";
import BulkEntry from "@/components/poshan/BulkEntry";
import { useRateCardsSync } from "@/lib/useRateCardsSync";
import { bumpDailyEntriesVersion } from "@/lib/dailyEntriesBus";
import MobileDailyEntry from "@/components/poshan/MobileDailyEntry";
import TemplateUploadDialog from "@/components/poshan/TemplateUploadDialog";
import FundEntryPrompt from "@/components/poshan/FundEntryPrompt";
import DailyEntryOdiaGuide from "@/components/poshan/DailyEntryOdiaGuide";
import { Plus, Save, Trash2, RefreshCw, Lock, LockOpen, Sparkles, AlertTriangle, CalendarDays, LayoutGrid, Pencil, Users, Utensils, Info, Archive, ChevronDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { dailyApi, enrollmentApi, financialYearFromMonth } from "@/lib/setupApi";
import { monthLockApi } from "@/lib/profilesApi";
import { useActiveSchool } from "@/lib/activeSchool";
import {
  buildDayBreakdown,
  daysOfMonth,
  ingredientsForDailyEntry,
  MONTH_LIST,
  TARGET_COST,
  LADOO_COST_PER_STUDENT,
} from "@/lib/mealPlan";
import { money, qty, toNumber, ingredientAmount } from "@/lib/poshanConstants";
import { NO_MDM_REASONS, isNoMdmDay, isHolidayDay, isMealDay, reasonLabel } from "@/lib/dayKind";
import { buildDailyPayload, saveErrorMessage, scrollReasonBlockIntoView } from "@/lib/dailyPayload";

function todayParts() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

function emptyDraft(schoolId, dateStr) {
  return {
    schoolId,
    date: dateStr,
    entryType: "mdm",
    dayKind: "working", // working | holiday — when holiday, all input fields are disabled but Save still works
    attendance: { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 },
    ingredients: [],
    notes: "",
  };
}

const WEEKDAY_OD = ["ରବିବାର", "ସୋମବାର", "ମଙ୍ଗଳବାର", "ବୁଧବାର", "ଗୁରୁବାର", "ଶୁକ୍ରବାର", "ଶନିବାର"];

function formatDateIN(iso) {
  // yyyy-mm-dd → { dmy: "dd/mm/yyyy", weekday: Odia name }
  if (!iso || typeof iso !== "string" || iso.length < 10) return { dmy: iso || "", weekday: "" };
  const [y, m, d] = iso.split("-");
  const dt = new Date(`${iso}T00:00:00`);
  return { dmy: `${d}/${m}/${y}`, weekday: WEEKDAY_OD[dt.getDay()] || "" };
}

export default function DailyEntry() {
  useRateCardsSync();
  const { active, profiles } = useActiveSchool();
  const schoolId = active?.id || "";
  const todayP = todayParts();
  const [year, setYear] = useState(todayP.year);
  const [month, setMonth] = useState(todayP.month);
  const [date, setDate] = useState(`${todayP.year}-${String(todayP.month).padStart(2, "0")}-${String(todayP.day).padStart(2, "0")}`);
  const [draft, setDraft] = useState(emptyDraft(schoolId, date));
  const [entries, setEntries] = useState([]);
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ingTab, setIngTab] = useState("total");
  const [mode, setMode] = useState("single"); // single | bulk
  // iter157 — Upload Filled Template dialog toggle
  const [uploadOpen, setUploadOpen] = useState(false);

  // Academic Year derived from selected (year, month). Used to fetch the
  // year-wise enrollment snapshot so headcount defaults stay accurate
  // across FY boundaries (e.g. April rollover).
  const academicYear = useMemo(() => financialYearFromMonth(year, month), [year, month]);
  const [enrollmentRow, setEnrollmentRow] = useState(null);
  useEffect(() => {
    if (!schoolId) { setEnrollmentRow(null); return; }
    let cancelled = false;
    enrollmentApi.get(schoolId, academicYear)
      .then((d) => { if (!cancelled) setEnrollmentRow(d || null); })
      .catch(() => { if (!cancelled) setEnrollmentRow(null); });
    return () => { cancelled = true; };
  }, [schoolId, academicYear]);

  // Effective enrollment used for Dry Ration defaults + AY summary display.
  // Prefer year-wise snapshot; fall back to active school profile.
  const ayEnrollment = useMemo(() => ({
    balVatika: toNumber(enrollmentRow?.balVatika ?? active?.balVatikaEnrollment),
    primary: toNumber(enrollmentRow?.primary ?? active?.primaryEnrollment),
    upperPrimary: toNumber(enrollmentRow?.upperPrimary ?? active?.upperPrimaryEnrollment),
    highSchool: toNumber(enrollmentRow?.highSchool ?? active?.highSchoolEnrollment),
  }), [enrollmentRow, active]);
  const ayEnrollmentTotal = ayEnrollment.balVatika + ayEnrollment.primary + ayEnrollment.upperPrimary + ayEnrollment.highSchool;

  // Adjust active date when month changes
  useEffect(() => {
    const days = daysOfMonth(year, month - 1);
    if (days.length === 0) return;
    if (!days.includes(date)) {
      setDate(days[0]);
    }
  }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load month entries + lock state
  const refresh = async () => {
    if (!schoolId) return;
    try {
      setLoading(true);
      const monthFromDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthToDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      const [eList, lList] = await Promise.all([
        dailyApi.list({ schoolId, fromDate: monthFromDate, toDate: monthToDate }),
        monthLockApi.list(schoolId),
      ]);
      setEntries(eList);
      setLocks(lList);
    } catch (err) {
      toast.error("Could not load entries");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, year, month]);

  // Sync draft when date or entries change
  useEffect(() => {
    const existing = entries.find((e) => e.schoolId === schoolId && e.date === date);
    if (existing) {
      setDraft({
        ...emptyDraft(schoolId, date),
        ...existing,
        dayKind: existing.dayKind || "working",
        attendance: { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0, ...(existing.attendance || {}) },
      });
    } else {
      setDraft(emptyDraft(schoolId, date));
    }
  }, [date, entries, schoolId]);

  const lockForMonth = locks.find((l) => l.year === year && l.month === month);
  const isHardLocked = lockForMonth?.lockLevel === "hard";
  const isSoftLocked = lockForMonth?.lockLevel === "soft";
  const draftIsNew = !draft.id;
  // Soft lock: no NEW entries, edits allowed.  Hard lock: no edits at all.
  const blockedNew = isSoftLocked && draftIsNew;
  const blockedEdit = isHardLocked;
  const blocked = blockedNew || blockedEdit;
  // When the user marks the day a Holiday, every input becomes read-only —
  // but the Save button stays enabled so we can persist a holiday marker.
  const isHoliday = draft.dayKind === "holiday";
  // iter154 — Working day · MDM not served. All meal-related inputs
  // (type / attendance / ingredients) hide and attendance saves as 0s.
  const isNoMdm = draft.dayKind === "working_no_mdm";
  const inputsBlocked = blocked || isHoliday || isNoMdm;
  // MDM Not Served / Holiday and Dry Ration are mutually exclusive: hide every meal section.
  const mealHidden = isHoliday || isNoMdm;

  const breakdown = useMemo(() => buildDayBreakdown(date, draft.attendance), [date, draft.attendance]);
  const monthDays = useMemo(() => daysOfMonth(year, month - 1), [year, month]);

  const updateAttendance = (key, value) =>
    setDraft((d) => ({ ...d, attendance: { ...d.attendance, [key]: toNumber(value) } }));

  const autoFillFromMealPlan = () => {
    const auto = ingredientsForDailyEntry(date, draft.attendance);
    if (auto.length === 0) {
      toast.error("Pick a working day with attendance > 0");
      return;
    }
    setDraft((d) => ({ ...d, ingredients: auto }));
    toast.success("Auto-filled from meal plan");
  };

  const updateIngredient = (idx, field, value) =>
    setDraft((d) => ({
      ...d,
      ingredients: d.ingredients.map((ing, i) =>
        i === idx ? { ...ing, [field]: field === "name" || field === "unit" ? value : toNumber(value) } : ing,
      ),
    }));

  const addIngredient = () =>
    setDraft((d) => ({
      ...d,
      ingredients: [
        ...d.ingredients,
        { id: `ing-${Date.now()}`, name: "", unit: "kg", quantity: 0, rate: 0, isAuto: false },
      ],
    }));

  const removeIngredient = (idx) =>
    setDraft((d) => ({ ...d, ingredients: d.ingredients.filter((_, i) => i !== idx) }));

  const dayTotalsAmount = useMemo(
    () => (draft.ingredients || []).reduce((s, i) => s + ingredientAmount(i), 0),
    [draft.ingredients],
  );

  const handleSave = async () => {
    if (!schoolId) {
      toast.error("Pick an active school first");
      return;
    }
    if (blocked) {
      toast.error(blockedEdit ? "This month is hard-locked" : "Cannot create new entries — month is locked");
      return;
    }
    // iter149 — Attach a stable entryId so the backend upsert is idempotent
    // whether we send online now or replay from the IDB queue later.
    const entryId = draft.id || (await import("@/lib/dailyEntryQueue")).newEntryId();
    const payload = buildDailyPayload({ ...draft, schoolId, date, id: entryId });
    const canUseNetwork = typeof navigator === "undefined" ? true : navigator.onLine;
    try {
      setSaving(true);
      if (!canUseNetwork) throw new Error("offline");
      await dailyApi.upsert(payload, entryId);
      toast.success("Daily entry saved — reports & MDM Bill will refresh");
      // iter151 — Stamp "first save done" so PWAInstallBanner can fire
      // (per spec: show after 1st successful save OR 2nd visit).
      try { localStorage.setItem("mdm.firstSaveDone", String(Date.now())); } catch { /* no-op */ }
      bumpDailyEntriesVersion();
      await refresh();
    } catch (err) {
      // iter149 — Offline fallback. Queue the save to IndexedDB and show
      // a saffron "Saved on phone — will sync when online" toast. The
      // `online` event handler (useEffect below) flushes the queue when
      // the signal returns.
      const isOffline = (!canUseNetwork) || (err && err.message === "Network Error");
      if (isOffline) {
        try {
          const q = await import("@/lib/dailyEntryQueue");
          await q.enqueueDailyEntry({ entryId, schoolId, date, payload });
          const b = await import("@/components/mobile/OfflineBanner");
          b.bumpOfflineQueue();
          toast.message("Saved on this phone — will sync when back online.", { icon: "📴" });
          // Optimistic local state: pretend the save worked so the UI
          // doesn't bounce back to the previous values.
          bumpDailyEntriesVersion();
        } catch (queueErr) {
          toast.error("Save failed — please retry");
          // eslint-disable-next-line no-console
          console.error("[daily] queue failure", queueErr);
        }
      } else {
        toast.error(saveErrorMessage(err));
        if (payload.dayKind === "working_no_mdm") scrollReasonBlockIntoView();
        // eslint-disable-next-line no-console
        console.error("[daily] save failed", err?.response?.status, err?.response?.data, err);
      }
    } finally {
      setSaving(false);
    }
  };

  // iter149 — Flush the offline queue whenever the browser reports it's
  // back online. Items for a different schoolId are dropped. Capped at 5
  // attempts per item before surfacing a "Couldn't sync" state in the
  // OfflineBanner.
  useEffect(() => {
    if (!schoolId) return undefined;
    let aborted = false;
    const flush = async () => {
      try {
        const [{ flushQueue }, { bumpOfflineQueue }] = await Promise.all([
          import("@/lib/dailyEntryQueue"),
          import("@/components/mobile/OfflineBanner"),
        ]);
        const summary = await flushQueue({ schoolId, postFn: dailyApi.upsert });
        if (aborted) return;
        bumpOfflineQueue();
        if (summary.sent > 0) {
          toast.success(`Synced ${summary.sent} offline entry${summary.sent === 1 ? "" : "ies"}.`);
          bumpDailyEntriesVersion();
          await refresh();
        }
        if (summary.dropped > 0) {
          toast.message(`Dropped ${summary.dropped} offline entry${summary.dropped === 1 ? "" : "ies"} from a different school.`);
        }
        if (summary.stuck > 0) {
          toast.error(`${summary.stuck} offline entry${summary.stuck === 1 ? "" : "ies"} couldn't sync after 5 tries. Open Daily Entry to retry.`);
        }
      } catch { /* idb unavailable */ }
    };
    if (typeof navigator !== "undefined" && navigator.onLine) flush();
    const onOnline = () => flush();
    window.addEventListener("online", onOnline);
    return () => { aborted = true; window.removeEventListener("online", onOnline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const handleDelete = async (id) => {
    try {
      await dailyApi.remove(id);
      toast.success("Entry deleted");
      bumpDailyEntriesVersion();
      await refresh();
    } catch (err) {
      toast.error("Delete failed");
      console.error(err);
    }
  };

  const setLockLevel = async (level) => {
    if (!schoolId) return;
    try {
      if (level === null) {
        // unlock
        if (lockForMonth?.id) await monthLockApi.remove(lockForMonth.id);
        toast.success("Month unlocked");
      } else {
        await monthLockApi.upsert({ schoolId, year, month, lockLevel: level });
        toast.success(level === "hard" ? "Month hard-locked" : "Month soft-locked");
      }
      await refresh();
    } catch (err) {
      toast.error("Lock action failed");
      console.error(err);
    }
  };

  // iter131 — wipe every daily entry for the selected (year, month). Hard-locked
  // months are rejected by the backend; we also surface the count for clarity.
  const [resetting, setResetting] = useState(false);
  // iter132 — Reset Range state. fromDate defaults to the 1st of the selected
  // month, toDate defaults to "today (clamped into the selected month)" so
  // schools usually just hit Confirm without retyping anything.
  const monthIso = `${year}-${String(month).padStart(2, "0")}`;
  const todayIso = new Date().toISOString().slice(0, 10);
  const defaultFrom = `${monthIso}-01`;
  const defaultTo = todayIso.startsWith(monthIso) ? todayIso : `${monthIso}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
  const [rangeFrom, setRangeFrom] = useState(defaultFrom);
  const [rangeTo, setRangeTo] = useState(defaultTo);
  // Re-seed the range whenever the user flips year/month so it always points
  // at a sensible window for the currently-viewed month.
  useEffect(() => {
    setRangeFrom(`${monthIso}-01`);
    const lastDay = new Date(year, month, 0).getDate();
    setRangeTo(todayIso.startsWith(monthIso) ? todayIso : `${monthIso}-${String(lastDay).padStart(2, "0")}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // Count of entries that fall inside the chosen range — purely for the
  // confirmation copy; the server is still the source of truth on delete.
  const rangeEntryCount = useMemo(
    () => entries.filter((e) => e.date >= rangeFrom && e.date <= rangeTo).length,
    [entries, rangeFrom, rangeTo],
  );

  const handleResetMonth = async () => {
    if (!schoolId) { toast.error("Pick an active school first"); return; }
    if (isHardLocked) { toast.error("Hard-locked month cannot be reset — unlock first"); return; }
    try {
      setResetting(true);
      const res = await dailyApi.resetMonth({ schoolId, year, month });
      toast.success(`Reset complete — ${res.count} ${res.count === 1 ? "entry" : "entries"} deleted`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  const handleResetRange = async () => {
    if (!schoolId) { toast.error("Pick an active school first"); return; }
    if (!rangeFrom || !rangeTo) { toast.error("Pick both From and To dates"); return; }
    if (rangeFrom > rangeTo) { toast.error("From-date must be on or before To-date"); return; }
    try {
      setResetting(true);
      const res = await dailyApi.resetRange({ schoolId, fromDate: rangeFrom, toDate: rangeTo });
      toast.success(`Range reset — ${res.count} ${res.count === 1 ? "entry" : "entries"} deleted between ${res.fromDate} and ${res.toDate}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  // iter140 — replaced (Year + Month) dual-dropdown with a single Month-Year
  // picker that iterates over the 12 months of the selected Academic Year
  // (April → March). Each option's value encodes BOTH year and month
  // (e.g. "2025-4"), so the rest of the app (reports, vendors, CCH) keeps
  // its (year, month) state untouched and continues to receive correct
  // values via daily_entries.date.
  const ayMonthYearChoices = useMemo(() => {
    const [startStr] = (academicYear || "").split("-");
    const start = Number(startStr);
    if (!Number.isFinite(start)) return [];
    const out = [];
    // April (4) → December (12) of start year
    for (let m = 4; m <= 12; m++) {
      out.push({ value: `${start}-${m}`, year: start, month: m, label: `${MONTH_LIST[m - 1].label} ${start}` });
    }
    // January (1) → March (3) of start year + 1
    for (let m = 1; m <= 3; m++) {
      out.push({ value: `${start + 1}-${m}`, year: start + 1, month: m, label: `${MONTH_LIST[m - 1].label} ${start + 1}` });
    }
    return out;
  }, [academicYear]);

  const monthYearValue = `${year}-${month}`;

  const onMonthYearChange = (v) => {
    const opt = ayMonthYearChoices.find((x) => x.value === v);
    if (!opt) return;
    setYear(opt.year);
    setMonth(opt.month);
  };

  // Academic-Year choices — covers ~5 FYs around today.
  const academicYearChoices = useMemo(() => {
    const cur = todayP.year;
    const list = [];
    for (let i = -2; i <= 2; i++) {
      const s = cur + i;
      list.push(`${s}-${String((s + 1) % 100).padStart(2, "0")}`);
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayP.year]);

  // Switching AY snaps year+month to the AY's first calendar month (April of
  // start-year) unless the current selection is already inside that AY.
  const onAcademicYearChange = (ay) => {
    const [startStr] = (ay || "").split("-");
    const start = Number(startStr);
    if (!Number.isFinite(start)) return;
    // Apr-Dec of startYear OR Jan-Mar of (startYear+1) → inside AY.
    const inside = (year === start && month >= 4) || (year === start + 1 && month <= 3);
    if (!inside) {
      setYear(start);
      setMonth(4); // April
    }
  };

  if (profiles.length === 0) {
    return (
      <main className="min-h-screen bg-sand-50 text-ink-900">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">Setup needed</p>
          <h1 className="mt-3 font-heading text-3xl font-semibold">Add a school first</h1>
          <p className="mt-3 text-ink-700">Daily Entry needs an active school. Open Setup to add one.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />

      {/* Fund Management nudge — pops once per (school, year, month) when no
          fund-balance record exists yet. Skip-for-now hides it for the rest
          of the session. */}
      <FundEntryPrompt
        schoolId={schoolId}
        year={year}
        month={month}
        onYearChange={setYear}
        onMonthChange={setMonth}
      />

      {/* ============================================================
          MOBILE / TABLET LAYOUT (< lg) — matches the supplied mockup.
          Reuses the same state + handlers as the desktop layout above.
          ============================================================ */}
      <div className="md:hidden mx-auto max-w-3xl px-3 pt-3">
        <DailyEntryOdiaGuide />
      </div>
      <MobileDailyEntry
        active={active}
        year={year} month={month} date={date}
        setYear={setYear} setMonth={setMonth} setDate={setDate}
        draft={draft}
        mode={mode} setMode={setMode}
        monthDays={monthDays}
        entries={entries}
        breakdown={breakdown}
        dayTotalsAmount={dayTotalsAmount}
        updateAttendance={updateAttendance}
        addIngredient={addIngredient}
        removeIngredient={removeIngredient}
        updateIngredient={updateIngredient}
        autoFillFromMealPlan={autoFillFromMealPlan}
        handleSave={handleSave}
        saving={saving}
        blocked={blocked}
        inputsBlocked={inputsBlocked}
        isHoliday={isHoliday}
        blockedEdit={blockedEdit}
        blockedNew={blockedNew}
        isHardLocked={isHardLocked}
        isSoftLocked={isSoftLocked}
        lockForMonth={lockForMonth}
        setLockLevel={setLockLevel}
        ayMonthYearChoices={ayMonthYearChoices}
        monthYearValue={monthYearValue}
        onMonthYearChange={onMonthYearChange}
        refresh={refresh}
        setDraft={setDraft}
      />

      <div className="mx-auto hidden max-w-7xl px-4 py-10 sm:px-6 md:block lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-ink-700">Day-by-day kitchen log</p>
            <h1 className="mt-3 font-heading text-4xl font-semibold tracking-tight text-ink-900 sm:text-5xl">
              Daily Entry
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-700">
              School: <b className="text-ink-900">{active?.schoolName}</b>{" "}
              <span className="text-ink-700">· UDISE {active?.udiseCode}</span>
            </p>
          </div>
        </div>

        {/* iter109 — Odia step-by-step guide */}
        <DailyEntryOdiaGuide />

        {/* Year / Month / Lock Controls */}
        <section
          data-testid="month-controls"
          className="mb-6 rounded-xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-6"
        >
          {/* Mode toggle: Single Day vs Bulk Entry */}
          <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="entry-mode-toggle">
            <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">
              Entry Mode
            </span>
            <div className="inline-flex rounded-full border border-border bg-sand-50 p-1">
              {[
                { v: "single", label: "Single Day", Icon: CalendarDays },
                { v: "bulk", label: "Bulk Entry (whole month)", Icon: LayoutGrid },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`entry-mode-${opt.v}`}
                  onClick={() => setMode(opt.v)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-150 ${
                    mode === opt.v ? "bg-clay text-white shadow-sm" : "text-ink-700 hover:text-clay"
                  }`}
                >
                  <opt.Icon className="h-3.5 w-3.5" />
                  {opt.label}
                </button>
              ))}
            </div>
            {mode === "bulk" && (
              <span className="ml-auto rounded-full bg-moss/15 px-3 py-1 text-xs font-semibold text-moss-dark">
                Quick-fill a missed month in one go
              </span>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_1.4fr_auto] sm:items-end">
            <label className="block">
              <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Academic Year</span>
              <Select value={academicYear} onValueChange={onAcademicYearChange}>
                <SelectTrigger data-testid="daily-academic-year-select" className="h-11 bg-white border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {academicYearChoices.map((ay) => (
                    <SelectItem key={ay} value={ay}>{ay}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">
                Month <span className="text-ink-700/60 font-normal normal-case tracking-normal">(of AY {academicYear})</span>
              </span>
              <Select value={monthYearValue} onValueChange={onMonthYearChange}>
                <SelectTrigger data-testid="daily-month-year-select" className="h-11 bg-white border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ayMonthYearChoices.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`daily-month-year-opt-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="daily-template-download"
                onClick={async () => {
                  try {
                    const { downloadDailyEntryTemplate } = await import("@/lib/excelTemplates");
                    await downloadDailyEntryTemplate({ year, month, entries });
                    toast.success("Daily Entry template downloaded");
                  } catch (err) {
                    console.error(err);
                    toast.error("Could not build template");
                  }
                }}
                className="gap-1.5"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Monthly Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="daily-template-upload-btn"
                onClick={() => setUploadOpen(true)}
                className="gap-1.5"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Upload Filled
              </Button>
              {!lockForMonth && (
                <>
                  <Button onClick={() => setLockLevel("soft")} data-testid="soft-lock-btn" variant="outline" size="sm" className="gap-2">
                    <Lock className="h-3.5 w-3.5" /> Soft Lock
                  </Button>
                  <Button onClick={() => setLockLevel("hard")} data-testid="hard-lock-btn" size="sm" className="gap-2 bg-ink-900 text-white hover:bg-ink-700">
                    <Lock className="h-3.5 w-3.5" /> Hard Lock
                  </Button>
                </>
              )}
              {lockForMonth && (
                <>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    isHardLocked ? "bg-clay-50 text-clay-dark" : "bg-ochre/15 text-ochre"
                  }`}>
                    <Lock className="h-3 w-3" /> {isHardLocked ? "Hard Locked" : "Soft Locked"}
                  </span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="unlock-btn" className="gap-2">
                        <LockOpen className="h-3.5 w-3.5" /> Unlock
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Unlock {MONTH_LIST[month - 1].label} {year}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This re-enables new entries and edits for this month.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => setLockLevel(null)}>Unlock</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}

              {/* iter131 — Reset (delete) every daily entry for the selected month. */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isHardLocked || resetting || entries.length === 0}
                    className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    data-testid="reset-month-btn"
                    title={
                      isHardLocked
                        ? "Hard-locked month — unlock to reset"
                        : entries.length === 0
                        ? "No entries to delete"
                        : `Delete all ${entries.length} entries for this month`
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Reset Month{entries.length > 0 ? ` (${entries.length})` : ""}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="reset-month-dialog" className="sm:max-w-md">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
                      <AlertTriangle className="h-5 w-5" />
                      Reset daily entries
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Choose <b>Whole Month</b> to wipe every entry for {MONTH_LIST[month - 1].label} {year},
                      or <b>Date Range</b> to delete only a specific window
                      (e.g. last week's mistakes). <b>This cannot be undone.</b>
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <Tabs defaultValue="month" className="mt-2">
                    <TabsList className="grid w-full grid-cols-2" data-testid="reset-tabs">
                      <TabsTrigger value="month" data-testid="reset-tab-month">Whole Month</TabsTrigger>
                      <TabsTrigger value="range" data-testid="reset-tab-range">Date Range</TabsTrigger>
                    </TabsList>

                    <TabsContent value="month" className="space-y-3 pt-3">
                      <div className="rounded-md border border-rose-200 bg-rose-50/60 p-3 text-sm text-ink-800">
                        Will delete <b>{entries.length}</b>{" "}
                        {entries.length === 1 ? "entry" : "entries"} for
                        <b> {MONTH_LIST[month - 1].label} {year}</b>.
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="reset-month-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleResetMonth}
                          className="bg-rose-600 text-white hover:bg-rose-700"
                          data-testid="reset-month-confirm"
                          disabled={entries.length === 0}
                        >
                          Yes, delete all {entries.length} {entries.length === 1 ? "entry" : "entries"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </TabsContent>

                    <TabsContent value="range" className="space-y-3 pt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <label className="text-xs font-semibold text-ink-700">
                          From-date
                          <input
                            type="date"
                            value={rangeFrom}
                            min={`${monthIso}-01`}
                            onChange={(e) => setRangeFrom(e.target.value)}
                            className="mt-1 w-full rounded-md border border-border bg-white px-2 py-2 text-sm"
                            data-testid="reset-range-from"
                          />
                        </label>
                        <label className="text-xs font-semibold text-ink-700">
                          To-date
                          <input
                            type="date"
                            value={rangeTo}
                            max={`${monthIso}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`}
                            onChange={(e) => setRangeTo(e.target.value)}
                            className="mt-1 w-full rounded-md border border-border bg-white px-2 py-2 text-sm"
                            data-testid="reset-range-to"
                          />
                        </label>
                      </div>
                      <div className="rounded-md border border-rose-200 bg-rose-50/60 p-3 text-sm text-ink-800">
                        Will delete <b data-testid="reset-range-count">{rangeEntryCount}</b>{" "}
                        {rangeEntryCount === 1 ? "entry" : "entries"} between
                        <b> {rangeFrom}</b> and <b>{rangeTo}</b>.
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="reset-range-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleResetRange}
                          className="bg-rose-600 text-white hover:bg-rose-700"
                          data-testid="reset-range-confirm"
                          disabled={!rangeFrom || !rangeTo || rangeFrom > rangeTo || rangeEntryCount === 0}
                        >
                          Yes, delete {rangeEntryCount} {rangeEntryCount === 1 ? "entry" : "entries"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </TabsContent>
                  </Tabs>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Academic-Year enrollment summary — pulled from year-wise enrollment
              snapshot for {academicYear}; falls back to school profile when no
              snapshot exists. These numbers drive Dry-Ration headcount defaults. */}
          <div
            data-testid="ay-enrollment-summary"
            className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-sand-50 px-4 py-3 text-sm"
          >
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-700">
              Enrolment · AY {academicYear}
            </span>
            {[
              { k: "balVatika", label: "Bal Vatika" },
              { k: "primary", label: "Primary" },
              { k: "upperPrimary", label: "Upper Primary" },
              { k: "highSchool", label: "High School" },
            ].map((c) => (
              <span
                key={c.k}
                data-testid={`ay-enrol-${c.k}`}
                className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs"
              >
                <span className="text-ink-700">{c.label}</span>
                <b className="text-ink-900 font-mono">{ayEnrollment[c.k]}</b>
              </span>
            ))}
            <span className="ml-auto rounded-full bg-moss/15 px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-moss-dark">
              Total <b className="ml-1 font-mono">{ayEnrollmentTotal}</b>
            </span>
            {!enrollmentRow && (
              <span className="rounded-full bg-ochre/15 px-2.5 py-1 text-[0.7rem] font-semibold text-ochre" title="No year-wise enrollment saved for this AY — using school profile defaults">
                Profile default
              </span>
            )}
          </div>

          {/* Date strip — Single Day mode only */}
          {mode === "single" && (
          <div className="mt-5">
            <p className="mb-2 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Pick a date</p>
            <div className="flex flex-wrap gap-1.5" data-testid="date-strip">
              {monthDays.map((d) => {
                const dow = new Date(`${d}T00:00:00`).getDay();
                const entryForDay = entries.find((e) => e.date === d);
                const hasEntry = !!entryForDay;
                const isDREntry = entryForDay?.entryType === "dryration";
                const isOffEntry = hasEntry && !isMealDay(entryForDay);
                const dayNum = Number(d.slice(8, 10));
                const isActive = d === date;
                return (
                  <button
                    key={d}
                    onClick={() => setDate(d)}
                    data-testid={`date-btn-${d}`}
                    data-entry-type={isOffEntry ? (isHolidayDay(entryForDay) ? "HOLIDAY" : "NO_MDM") : entryForDay?.entryType === "dryration" ? "DR" : entryForDay ? "MDM" : undefined}
                    className={`flex flex-col items-center rounded-md border px-2 py-1.5 text-xs font-medium transition-all duration-200 ${
                      isActive ? "border-clay bg-clay text-white" :
                      hasEntry
                        ? (isOffEntry
                            ? "border-slate-300 bg-slate-100 text-slate-600 hover:border-slate-400"
                            : isDREntry
                            ? "border-ochre bg-ochre/15 text-ochre hover:border-ochre"
                            : "border-moss bg-moss/15 text-moss-dark hover:border-moss-dark")
                        : dow === 0 ? "border-border bg-sand-100 text-ink-700/70" :
                      "border-border bg-white text-ink-900 hover:border-clay"
                    }`}
                  >
                    <span className="text-[0.65rem] uppercase tracking-wider opacity-80">
                      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dow]}
                    </span>
                    <span className="text-base font-semibold">{dayNum}</span>
                    {hasEntry && (
                      <span className={`mt-0.5 rounded-full px-1 text-[0.5rem] font-bold leading-none ${
                        isActive
                          ? "bg-white/30 text-white"
                          : isOffEntry ? "bg-slate-400 text-white" : isDREntry ? "bg-ochre text-white" : "bg-moss text-white"
                      }`}>{isOffEntry ? (isHolidayDay(entryForDay) ? "HOL" : "NO MDM") : isDREntry ? "DR" : "MDM"}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          )}
        </section>

        {blockedEdit && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-clay bg-clay-50 px-4 py-3 text-sm text-clay-dark" data-testid="hard-lock-banner">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>This month is <b>hard-locked</b> — entries are read-only. Unlock above to edit.</span>
          </div>
        )}
        {blockedNew && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-ochre bg-ochre/10 px-4 py-3 text-sm text-ochre" data-testid="soft-lock-banner">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>This month is <b>soft-locked</b> — you can edit existing entries but cannot add new ones for new dates.</span>
          </div>
        )}

        {mode === "bulk" ? (
          <BulkEntry
            schoolId={schoolId}
            active={active}
            year={year}
            month={month}
            existingEntries={entries}
            blockedNew={isSoftLocked || isHardLocked}
            blockedEdit={blockedEdit}
            onSaved={refresh}
          />
        ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          <section className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8">
            {/* Meal of the day */}
            {breakdown && (
              <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg bg-sand-100 px-4 py-3 text-sm" data-testid="meal-of-day-banner">
                <span className="rounded-full bg-clay/15 px-2.5 py-0.5 text-xs font-semibold text-clay">{breakdown.mealName}</span>
                <span className="text-ink-700">
                  Per-student target: Primary <b className="text-ink-900">{money(TARGET_COST.primary)}</b> · Upper Primary <b className="text-ink-900">{money(TARGET_COST.upperPrimary)}</b>
                  {breakdown.ladoo && <> · Ladoo +<b className="text-ink-900">{money(LADOO_COST_PER_STUDENT)}</b></>}
                </span>
              </div>
            )}

            {/* Working Day / Holiday selector — shown above the existing
                Day Type (MDM / Dry Ration) toggle. Defaults to Working Day;
                picking Holiday disables every input below but keeps the Save
                button enabled so the user can still persist a holiday marker
                (for reports / MDCF day counting). */}
            <div
              className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-clay/30 bg-clay/5 p-3"
              data-testid="working-holiday-toggle"
              data-active-daykind={draft.dayKind}
            >
              <div className="min-w-0">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-clay-dark">
                  📅 ଦିନ · Day
                </p>
                <p className="font-heading text-base font-bold text-ink-900" data-testid="daily-date-pretty">
                  {(() => { const f = formatDateIN(date); return `${f.weekday} · ${f.dmy}`; })()}
                </p>
              </div>
              <div className="ml-auto inline-flex rounded-full border border-border bg-white p-1">
                {[
                  { v: "working",         label: "MDM Served · ଭୋଜନ ପରିବେଷଣ",       activeBg: "bg-moss text-white" },
                  { v: "working_no_mdm",  label: "No MDM · ପରିବେଷଣ ହୋଇନାହିଁ",      activeBg: "bg-ochre text-white" },
                  { v: "holiday",         label: "Holiday · ଛୁଟି",                   activeBg: "bg-clay text-white" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`daykind-${opt.v}`}
                    disabled={blocked}
                    onClick={() => {
                      if (blocked) return;
                      const next = opt.v;
                      setDraft((d) => {
                        // Every day-kind switch starts clean (new + existing entries alike).
                        const newDraft = {
                          ...d, dayKind: next, entryType: "mdm", ingredients: [],
                          attendance: { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 },
                          hadDryRation: next !== "working" && (d.entryType === "dryration" || !!d.hadDryRation),
                        };
                        if (next !== "working_no_mdm") {
                          // Clear no-MDM-only fields when switching away.
                          newDraft.noMdmReason = undefined;
                          newDraft.noMdmReasonOther = undefined;
                          newDraft.orderNo = undefined;
                          newDraft.orderDate = undefined;
                        }
                        return newDraft;
                      });
                      toast.success(
                        next === "holiday" ? "Marked as Holiday · ଛୁଟି ଦିନ" :
                        next === "working_no_mdm" ? "Working Day — No MDM served" :
                        "Marked as Working Day · କାର୍ଯ୍ୟ ଦିନ"
                      );
                    }}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-150 sm:text-sm ${
                      draft.dayKind === opt.v ? opt.activeBg : "text-ink-700 hover:text-clay"
                    } disabled:opacity-60`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* iter154 — No-MDM reason chips. Required when dayKind
                === "working_no_mdm". We render below the day-kind
                picker and ABOVE the holiday banner. */}
            {isNoMdm && (
              <div
                className="mb-4 rounded-xl border-2 border-ochre/40 bg-ochre/10 p-3"
                data-testid="no-mdm-reason-block"
              >
                <p className="mb-2 text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-ochre-dark">
                  ❗ Reason · କାରଣ <span className="text-rose-600">*</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {NO_MDM_REASONS.map((r) => {
                    const active = draft.noMdmReason === r.key;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        data-testid={`no-mdm-reason-${r.key}`}
                        onClick={() => setDraft((d) => ({ ...d, noMdmReason: r.key }))}
                        className={`min-h-[44px] rounded-lg border-2 px-3 text-xs font-semibold transition-all ${
                          active
                            ? "border-ochre bg-ochre text-white shadow"
                            : "border-slate-200 bg-white text-ink-800 hover:border-ochre/40"
                        }`}
                      >
                        {r.en} · <span className="opacity-80">{r.od}</span>
                      </button>
                    );
                  })}
                </div>
                {draft.noMdmReason === "other" && (
                  <input
                    type="text"
                    data-testid="no-mdm-reason-other-input"
                    value={draft.noMdmReasonOther || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, noMdmReasonOther: e.target.value }))}
                    placeholder="Describe the reason · କାରଣ ଲେଖନ୍ତୁ"
                    className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:border-ochre focus:outline-none"
                  />
                )}
                {draft.noMdmReason === "collector_order" && (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <input type="text" data-testid="no-mdm-order-no" placeholder="Order No." value={draft.orderNo || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, orderNo: e.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />
                    <input type="date" data-testid="no-mdm-order-date" value={draft.orderDate || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, orderDate: e.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />
                  </div>
                )}
                <input type="text" data-testid="no-mdm-remarks" placeholder="Remarks · ମନ୍ତବ୍ୟ (optional)" value={draft.remarks || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, remarks: e.target.value }))}
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />
                <input type="number" min="0" data-testid="no-mdm-students-record" placeholder="Students present (for record only · ହିସାବ ପାଇଁ ମାତ୍ର)"
                  value={draft.studentsPresentRecord ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, studentsPresentRecord: e.target.value === "" ? undefined : Number(e.target.value) }))}
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />
                <p className="mt-3 rounded-lg bg-ochre/15 px-3 py-2 text-[0.72rem] font-semibold text-ochre-dark">
                  ⚠️ No meal served today. Rice and cooking cost will not be deducted. <br/>
                  ଆଜି ଭୋଜନ ପରିବେଷଣ ହୋଇନାହିଁ। ଚାଉଳ ଓ ରନ୍ଧନ ଖର୍ଚ୍ଚ କାଟା ହେବ ନାହିଁ।
                </p>
                {draft.hadDryRation && (
                  <p className="mt-2 rounded-lg border border-ochre/40 bg-white px-3 py-2 text-[0.72rem] text-ink-800" data-testid="no-mdm-dr-note">
                    Dry ration distributed? Choose Working Day · MDM Served → Dry Ration instead.<br />
                    ଶୁଖିଲା ରାସନ ବଣ୍ଟନ ହୋଇଛି କି? ତେବେ "MDM Served" ବାଛି Dry Ration ବାଛନ୍ତୁ।
                  </p>
                )}
              </div>
            )}

            {/* Holiday banner — when the day is marked Holiday, all inputs
                below are read-only. Save still works. */}
            {isHoliday && (
              <div
                className="mb-5 flex flex-wrap items-start gap-2 rounded-xl border-2 border-clay/40 bg-clay/10 p-3 text-sm text-clay-dark"
                data-testid="holiday-banner"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <b>ଛୁଟି ଦିନ · Holiday selected.</b> ଆଜି ଖାଦ୍ୟ ପ୍ରଦାନ କରାଯାଇନଥିଲା — Attendance ଓ Ingredients entry disabled। Save ଦବାନ୍ତୁ ତାହାହେଲେ ଏହି ଦିନ Holiday ଭାବେ record ହେବ।
                </span>
              </div>
            )}

            {!mealHidden && (<>
            <div
              className={`mb-5 flex flex-wrap items-center gap-3 rounded-xl border p-3 transition-colors duration-200 ${
                draft.entryType === "dryration"
                  ? "border-ochre/40 bg-ochre/5"
                  : "border-moss/40 bg-moss/5"
              } ${isHoliday ? "pointer-events-none opacity-50" : ""}`}
              data-testid="entry-type-toggle"
              data-active-type={draft.entryType === "dryration" ? "DR" : "MDM"}
            >
              <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Day Type</span>
              <div className="inline-flex rounded-full border border-border bg-white p-1">
                {[
                  { v: "mdm", label: "MDM (Mid-Day Meal)", activeBg: "bg-moss text-white" },
                  { v: "dryration", label: "Dry Ration", activeBg: "bg-ochre text-white" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`entry-type-${opt.v}`}
                    onClick={() => {
                      if (blocked || isHoliday) return;
                      const next = opt.v;
                      setDraft((d) => {
                        // When switching to Dry Ration, default attendance to school enrolment
                        const newDraft = { ...d, entryType: next };
                        if (next === "dryration") {
                          newDraft.attendance = {
                            balVatika: ayEnrollment.balVatika,
                            primary: ayEnrollment.primary,
                            upperPrimary: ayEnrollment.upperPrimary,
                            highSchool: ayEnrollment.highSchool,
                          };
                        }
                        return newDraft;
                      });
                      toast.success(next === "dryration" ? "Switched to Dry Ration mode (uses enrolment)" : "Switched to MDM mode");
                    }}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-150 ${
                      draft.entryType === opt.v ? opt.activeBg : "text-ink-700 hover:text-clay"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span
                className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${
                  draft.entryType === "dryration"
                    ? "bg-ochre text-white"
                    : "bg-moss text-white"
                }`}
                data-testid="entry-type-badge"
              >
                {draft.entryType === "dryration" ? "DRY RATION" : "MDM"}
              </span>
              {draft.entryType === "dryration" && (
                <span className="rounded-full bg-ochre/15 px-3 py-1 text-xs font-semibold text-ochre" data-testid="dry-ration-banner">
                  Quantities use full AY enrolment, not attendance
                </span>
              )}
            </div>

            <h3 className="mb-3 font-heading text-xl font-semibold text-ink-900">
              {draft.entryType === "dryration" ? "Today's Enrolment (Dry Ration)" : "Today's Attendance"}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <TextField testId="daily-att-bv" label="Bal Vatika" type="number" value={draft.attendance.balVatika} onChange={(v) => updateAttendance("balVatika", v)} readOnly={inputsBlocked} />
              <TextField testId="daily-att-primary" label="Primary" type="number" value={draft.attendance.primary} onChange={(v) => updateAttendance("primary", v)} readOnly={inputsBlocked} />
              <TextField testId="daily-att-up" label="Upper Primary" type="number" value={draft.attendance.upperPrimary} onChange={(v) => updateAttendance("upperPrimary", v)} readOnly={inputsBlocked} />
              <TextField testId="daily-att-hs" label="High School" type="number" value={draft.attendance.highSchool} onChange={(v) => updateAttendance("highSchool", v)} readOnly={inputsBlocked} />
            </div>

            {breakdown && breakdown.mealType !== "manual" && (
              <div className="mt-5 space-y-3" data-testid="day-totals-card">
                <div className="overflow-x-auto rounded-lg border border-border bg-sand-50">
                  <table className="w-full min-w-[640px] border-collapse text-sm">
                    <thead className="bg-sand-100 text-left text-ink-700">
                      <tr>
                        <th className="p-2.5 text-xs font-semibold uppercase tracking-[0.16em]">Class</th>
                        <th className="p-2.5 text-xs font-semibold uppercase tracking-[0.16em]">Students</th>
                        <th className="p-2.5 text-xs font-semibold uppercase tracking-[0.16em]">Per Student</th>
                        <th className="p-2.5 text-xs font-semibold uppercase tracking-[0.16em]">Meal Cost</th>
                        <th className="p-2.5 text-xs font-semibold uppercase tracking-[0.16em]">Ladoo Cost</th>
                        <th className="p-2.5 text-xs font-semibold uppercase tracking-[0.16em]">Day Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdown.perClass || []).map((c) => (
                        <tr key={c.key} data-testid={`class-row-${c.key}`} className="border-t border-border">
                          <td className="p-2.5 font-semibold text-ink-900">{c.label}</td>
                          <td className="p-2.5 text-ink-900 font-mono">{qty(c.count)}</td>
                          <td className="p-2.5 text-ink-700">{money(c.perStudent)}</td>
                          <td className="p-2.5 text-ink-900">{money(c.mealCost)}</td>
                          <td className="p-2.5 text-ink-900">{money(c.ladooCost)}</td>
                          <td className="p-2.5 font-semibold text-clay">{money(c.dayCost)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-border bg-sand-100">
                        <th className="p-2.5 text-left text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">Total</th>
                        <th className="p-2.5 text-left font-mono text-ink-900">{qty(breakdown.primaryGroupCount + breakdown.upperGroupCount)}</th>
                        <th />
                        <th className="p-2.5 text-left text-ink-900">{money(breakdown.totals.mealCost)}</th>
                        <th className="p-2.5 text-left text-ink-900">{money(breakdown.totals.ladooCost)}</th>
                        <th className="p-2.5 text-left text-clay">{money(breakdown.totals.dayCost)}</th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading text-xl font-semibold text-ink-900">Today's Ingredients</h3>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={autoFillFromMealPlan} disabled={inputsBlocked} data-testid="auto-fill-btn" className="gap-2 bg-moss text-white hover:bg-moss-dark">
                  <Sparkles className="h-3.5 w-3.5" /> Auto-fill from Meal Plan
                </Button>
                <Button size="sm" variant="outline" onClick={addIngredient} disabled={inputsBlocked && ingTab === "total"} data-testid="daily-add-ingredient" className={`gap-2 ${ingTab !== "total" ? "hidden" : ""}`}>
                  <Plus className="h-3.5 w-3.5" /> Add ingredient
                </Button>
              </div>
            </div>

            {/* Class tab strip — Total (editable) + per-class read-only views */}
            {breakdown && breakdown.mealType !== "manual" && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ing-class-tabs">
                {[
                  { key: "total", label: "Total (Save)" },
                  ...(breakdown.perClass || []).filter((c) => c.count > 0).map((c) => ({ key: c.key, label: c.label, count: c.count })),
                ].map((t) => (
                  <button
                    key={t.key}
                    data-testid={`ing-class-tab-${t.key}`}
                    onClick={() => setIngTab(t.key)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                      ingTab === t.key
                        ? "border-clay bg-clay text-white"
                        : "border-border bg-white text-ink-900 hover:border-clay hover:text-clay"
                    }`}
                  >
                    {t.label}{t.count !== undefined ? ` (${t.count})` : ""}
                  </button>
                ))}
              </div>
            )}

            {ingTab === "total" ? (
            <div className="mt-3 overflow-x-auto rounded-xl border border-border" data-testid="ing-total-table">
              <table className="w-full min-w-[760px] border-collapse text-sm">
                <thead className="bg-sand-100 text-left text-ink-700">
                  <tr>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Ingredient</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Qty</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Unit</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Rate</th>
                    <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {draft.ingredients.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-ink-700">
                      No ingredients yet. Click <b>Auto-fill from Meal Plan</b> to populate from {breakdown?.mealName || "the day"}.
                    </td></tr>
                  )}
                  {draft.ingredients.map((ing, idx) => (
                    <tr key={ing.id || idx} className={`border-t border-border ${ing.isAuto ? "bg-moss/5" : ""}`}>
                      <td className="p-2"><input data-testid={`daily-ing-${idx}-name`} className="table-input min-w-[200px]" value={ing.name} placeholder="Ingredient name" disabled={inputsBlocked} onChange={(e) => updateIngredient(idx, "name", e.target.value)} /></td>
                      <td className="p-2"><input data-testid={`daily-ing-${idx}-qty`} className="table-input" type="number" value={ing.quantity} disabled={inputsBlocked} onChange={(e) => updateIngredient(idx, "quantity", e.target.value)} /></td>
                      <td className="p-2"><input className="table-input" value={ing.unit} disabled={inputsBlocked} onChange={(e) => updateIngredient(idx, "unit", e.target.value)} /></td>
                      <td className="p-2"><input data-testid={`daily-ing-${idx}-rate`} className="table-input" type="number" value={ing.rate} disabled={inputsBlocked} onChange={(e) => updateIngredient(idx, "rate", e.target.value)} /></td>
                      <td className="p-3 font-semibold text-clay">{money(ingredientAmount(ing))}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeIngredient(idx)} disabled={blocked} className="text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-sand-100">
                    <th colSpan={4} className="p-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">Day Total</th>
                    <th className="p-3 text-left text-clay">{money(dayTotalsAmount)}</th>
                    <th />
                  </tr>
                </tfoot>
              </table>
            </div>
            ) : (
              (() => {
                const cls = (breakdown?.perClass || []).find((c) => c.key === ingTab);
                if (!cls) return null;
                const lines = cls.lines || [];
                const rowAmount = (l) => Number((l.costPerStudent * cls.count).toFixed(2));
                const rowQty = (l) => {
                  // Flat-cost items (Masala & Salt, Fuel) now carry a real
                  // `qtyPerStudent` so the Qty (Total) column shows
                  // qtyPerStudent × count gm.
                  const perStudent = l.costMode === "flat" ? Number(l.qtyPerStudent || 0) : l.qty;
                  return Number((perStudent * cls.count).toFixed(3));
                };
                const totalAmt = lines.reduce((s, l) => s + rowAmount(l), 0);
                return (
                  <div className="mt-3 overflow-x-auto rounded-xl border border-border" data-testid={`ing-class-table-${ingTab}`}>
                    <p className="bg-sand-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">
                      {cls.label} — {cls.count} students × Rs {cls.perStudent.toFixed(2)} = {money(cls.dayCost)} (read-only · same calc as Item-wise Consumption)
                    </p>
                    <table className="w-full min-w-[760px] border-collapse text-sm">
                      <thead className="bg-sand-100 text-left text-ink-700">
                        <tr>
                          <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Ingredient</th>
                          <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Qty (Total)</th>
                          <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Unit</th>
                          <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Rate</th>
                          <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l) => (
                          <tr key={`${cls.key}-${l.item}`} className="border-t border-border">
                            <td className="p-3 font-medium text-ink-900">
                              {l.item}
                              {l.isLadoo && <span className="ml-2 rounded-full bg-ochre/15 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ochre">Ladoo</span>}
                              {l.isFree && <span className="ml-2 rounded-full bg-moss/15 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-moss-dark">Free</span>}
                            </td>
                            <td className="p-3 text-ink-900 font-mono">{rowQty(l) > 0 ? `${qty(rowQty(l))} ${l.unit}` : "—"}</td>
                            <td className="p-3 text-ink-700">{l.unit}</td>
                            <td className="p-3 text-ink-700">{l.rateLabel}</td>
                            <td className="p-3 font-semibold text-clay">{money(rowAmount(l))}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-border bg-sand-100">
                          <th colSpan={4} className="p-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">Class Total</th>
                          <th className="p-3 text-left text-clay">{money(totalAmt)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()
            )}

            </>)}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-700">
                {draft.id ? `Editing entry for ${date}.` : `New entry for ${date}.`}
              </p>
              <Button data-testid="daily-save-btn" onClick={handleSave} disabled={saving || blocked} className="gap-2 bg-clay text-white hover:bg-clay-dark">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : draft.id ? "Update Entry" : "Save Entry"}
              </Button>
            </div>

            {/* Item-wise Consumption (per student) — classwise tabs */}
            {!mealHidden && (
              <div className="mt-8">
                <ItemwiseConsumption breakdown={breakdown} />
              </div>
            )}
          </section>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start" data-testid="daily-entries-list">
            <section className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)]">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">{MONTH_LIST[month - 1].label} {year}</p>
                  <h2 className="mt-1 font-heading text-xl font-semibold text-ink-900">Entries this month</h2>
                </div>
                <Button size="sm" variant="outline" onClick={refresh} className="gap-2">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
              {loading && <ListSkeleton count={4} />}
              {!loading && entries.length === 0 && (
                <p className="rounded-lg bg-sand-100 px-4 py-3 text-sm text-ink-700">
                  No entries for {MONTH_LIST[month - 1].label} {year} yet.
                </p>
              )}
              <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {[...entries].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => {
                  const total = (e.ingredients || []).reduce((s, i) => s + ingredientAmount(i), 0);
                  const isCurrent = e.date === date;
                  const isDR = e.entryType === "dryration";
                  const isNoMdmE = isNoMdmDay(e);
                  const isHolidayE = isHolidayDay(e);
                  const att = e.attendance || {};
                  const attRows = [
                    { key: "bv", label: "BV", count: toNumber(att.balVatika) },
                    { key: "p", label: "P", count: toNumber(att.primary) },
                    { key: "up", label: "UP", count: toNumber(att.upperPrimary) },
                    { key: "hs", label: "HS", count: toNumber(att.highSchool) },
                  ];
                  const attTotal = attRows.reduce((s, r) => s + r.count, 0);
                  // iter154 — ledger badge + reason display for No-MDM
                  // and Holiday entries so the sidebar reflects dayKind.
                  const badgeLabel = isHolidayE ? "HOLIDAY" : isNoMdmE ? "NO MDM" : isDR ? "DR" : "MDM";
                  const badgeCls = isHolidayE
                    ? "bg-slate-500 text-white"
                    : isNoMdmE
                      ? "bg-slate-400 text-white"
                      : isDR ? "bg-ochre text-white" : "bg-moss text-white";
                  const reasonTxt = isNoMdmE
                    ? (e.noMdmReason === "other" ? (e.noMdmReasonOther || "Other") : reasonLabel(e.noMdmReason || "", "en"))
                    : "";
                  return (
                    <li
                      key={e.id}
                      data-testid={`daily-entry-${e.date}`}
                      data-entry-type={isDR ? "DR" : isNoMdmE ? "NO_MDM" : isHolidayE ? "HOLIDAY" : "MDM"}
                      data-day-kind={e.dayKind || "working"}
                      className={`flex items-start justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm transition-all duration-200 ${
                        isCurrent
                          ? "border-clay bg-clay-50"
                          : isHolidayE
                            ? "border-slate-300 bg-slate-50 hover:border-slate-400"
                            : isNoMdmE
                              ? "border-slate-300 bg-slate-50 hover:border-slate-400"
                              : isDR
                                ? "border-ochre/40 bg-ochre/5 hover:border-ochre"
                                : "border-moss/30 bg-moss/5 hover:border-moss"
                      }`}
                    >
                      <button onClick={() => { setMode("single"); setDate(e.date); }} className="flex-1 text-left">
                        <p className="font-semibold text-ink-900">
                          {e.date}
                          <span
                            className={`ml-2 rounded-full px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] ${badgeCls}`}
                            data-testid={`daily-entry-badge-${e.date}`}
                          >
                            {badgeLabel}
                          </span>
                        </p>
                        {isNoMdmE && reasonTxt && (
                          <p
                            className="mt-1 text-[0.7rem] font-medium italic text-slate-600"
                            data-testid={`daily-entry-reason-${e.date}`}
                          >
                            {reasonTxt}
                          </p>
                        )}
                        {/* Classwise beneficiary chips — surfaces the per-date
                            attendance so the sidebar becomes a scannable
                            month-at-a-glance instead of just totals.
                            Hidden on No-MDM / Holiday rows where attendance
                            is forced to zero. */}
                        {!isNoMdmE && !isHolidayE && (
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`daily-entry-att-${e.date}`}>
                          {attRows.map((r) => (
                            <span
                              key={r.key}
                              data-testid={`daily-entry-att-${e.date}-${r.key}`}
                              className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[0.65rem] font-medium ${
                                r.count > 0
                                  ? "bg-white text-ink-900 ring-1 ring-inset ring-moss/40"
                                  : "bg-sand-100 text-ink-500"
                              }`}
                              title={`${r.label} beneficiaries`}
                            >
                              <span className="font-semibold uppercase tracking-[0.08em] text-ink-700">{r.label}</span>
                              <span className="font-mono">{r.count}</span>
                            </span>
                          ))}
                          <span
                            className="inline-flex items-center gap-1 rounded-md bg-clay/10 px-1.5 py-0.5 text-[0.65rem] font-semibold text-clay"
                            data-testid={`daily-entry-att-total-${e.date}`}
                            title="Total beneficiaries"
                          >
                            <Users className="h-3 w-3" />
                            <span className="font-mono">{attTotal}</span>
                          </span>
                        </div>
                        )}
                        {!isNoMdmE && !isHolidayE && (
                          <p className="mt-1 text-xs text-ink-700">
                            {(e.ingredients || []).length} item{(e.ingredients || []).length === 1 ? "" : "s"} · {money(total)}
                          </p>
                        )}
                      </button>
                      <div className="flex shrink-0 items-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`daily-entry-edit-${e.date}`}
                        onClick={() => { setMode("single"); setDate(e.date); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        disabled={blockedEdit}
                        className="text-clay hover:bg-clay-50 hover:text-clay-dark"
                        title="Edit this entry"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`daily-entry-delete-${e.date}`} disabled={blockedEdit} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete entry for {e.date}?</AlertDialogTitle>
                            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(e.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            {breakdown && breakdown.mealType !== "manual" && breakdown.aggregate?.length > 0 && (
              <section className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="meal-plan-summary">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-moss-dark">Meal plan</p>
                <h2 className="mt-1 font-heading text-lg font-semibold text-ink-900">{breakdown.mealName}</h2>

                {/* Classwise per-student summary */}
                <div className="mt-3 space-y-1.5">
                  {(breakdown.perClass || []).map((c) => (
                    <div key={c.key} data-testid={`mealplan-class-${c.key}`} className="flex items-center justify-between gap-2 rounded-md bg-sand-50 px-2.5 py-1.5 text-xs">
                      <span className="text-ink-700">{c.label} ({qty(c.count)})</span>
                      <span className="text-ink-900 font-mono">
                        {money(c.perStudent)}/stu · <b className="text-clay">{money(c.dayCost)}</b>
                      </span>
                    </div>
                  ))}
                </div>

                <p className="mt-4 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Ingredients</p>
                <div className="mt-1 space-y-1 text-xs">
                  {breakdown.aggregate.slice(0, 10).map((row) => (
                    <div key={row.name} className="flex items-center justify-between gap-2 border-b border-dashed border-border pb-1">
                      <span className="text-ink-700">{row.name.replace(/_/g, " ")}</span>
                      <span className="text-ink-900 font-mono">
                        {qty(row.totalQty)} {row.unit} · {money(row.totalAmount)}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>
        )}
      </div>
      {uploadOpen && (
        <TemplateUploadDialog
          kind="daily"
          year={year}
          month={month}
          schoolId={schoolId}
          onClose={() => setUploadOpen(false)}
          onApplied={async () => { await refresh?.(); }}
        />
      )}
    </main>
  );
}
